import { useEffect, useState } from "react";
import { Trash2, UserPlus, Edit3, Stethoscope, User as UserIcon, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";
import { api, formatError } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const EMPTY = { name: "", email: "", role: "patient", specialty: "", license_number: "", password: "" };

function UserDialog({ open, onOpenChange, initial, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(initial || EMPTY); }, [initial, open]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.name || !form.email) { toast.error("Nombre y email obligatorios"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        const payload = { name: form.name, email: form.email, role: form.role, specialty: form.specialty || "", license_number: form.license_number || "" };
        if (form.password) payload.password = form.password;
        await api.patch(`/admin/users/${initial.id}`, payload);
        toast.success("Usuario actualizado");
      } else {
        const { data } = await api.post("/admin/users", { ...form });
        toast.success(form.password ? "Usuario creado" : `Usuario creado · contraseña temporal: ${data.temp_password}`);
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="user-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Editar usuario" : "Nuevo usuario"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nombre completo</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="user-form-name" /></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="user-form-email" /></div>
          <div>
            <Label>Rol</Label>
            <Select value={form.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="patient">Paciente</SelectItem>
                <SelectItem value="doctor">Doctor</SelectItem>
                <SelectItem value="admin">Administrador</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.role === "doctor" && (
            <>
              <div><Label>Especialidad</Label><Input value={form.specialty} onChange={(e) => set("specialty", e.target.value)} /></div>
              <div><Label>Nº Colegiado</Label><Input value={form.license_number} onChange={(e) => set("license_number", e.target.value)} /></div>
            </>
          )}
          <div>
            <Label>Contraseña {isEdit && <span className="text-xs text-muted-foreground">(opcional, dejar vacío para no cambiar)</span>}</Label>
            <Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder={isEdit ? "Dejar vacío para mantener" : "Mínimo 6 caracteres"} data-testid="user-form-password" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="user-form-submit">
            {saving ? "..." : (isEdit ? "Guardar" : "Crear")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UserList({ filter, refreshKey, onAction }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/users").then(({ data }) => setItems(data)).catch(() => {}); }, [refreshKey]);
  const filtered = items.filter((u) => filter === "all" || u.role === filter);
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este usuario?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Eliminado"); onAction(); }
    catch (e) { toast.error(formatError(e)); }
  };
  const ROLE_ICON = { doctor: Stethoscope, admin: ShieldCheck, patient: UserIcon };
  return (
    <div className="space-y-2">
      {filtered.map((u) => {
        const Icon = ROLE_ICON[u.role] || UserIcon;
        return (
          <div key={u.id} className="flex items-center justify-between py-3 px-3 border rounded-lg hover:bg-muted/30 transition-colors" data-testid={`user-${u.id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center ${u.role === "doctor" ? "bg-emerald-100 text-emerald-700" : u.role === "admin" ? "bg-purple-100 text-purple-700" : "bg-slate-100 text-slate-600"}`}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <div className="font-medium truncate">{u.name}</div>
                <div className="text-xs text-muted-foreground truncate">{u.email}{u.specialty ? ` · ${u.specialty}` : ""}{u.license_number ? ` · Col. ${u.license_number}` : ""}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Badge variant="outline">{u.role}</Badge>
              <Button size="icon" variant="ghost" onClick={() => onAction("edit", u)} data-testid={`edit-${u.id}`}><Edit3 className="w-4 h-4" /></Button>
              {u.role !== "admin" && (
                <Button size="icon" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => remove(u.id)} data-testid={`delete-${u.id}`}><Trash2 className="w-4 h-4" /></Button>
              )}
            </div>
          </div>
        );
      })}
      {filtered.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">Sin usuarios</div>}
    </div>
  );
}

function AdminAppointments({ refreshKey, onChange }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/appointments").then(({ data }) => setItems(data)).catch(() => {}); }, [refreshKey]);
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta cita?")) return;
    try { await api.delete(`/admin/appointments/${id}`); toast.success("Eliminada"); onChange(); }
    catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="space-y-2 max-h-[500px] overflow-y-auto">
      {items.map((a) => (
        <div key={a.id} className="flex items-center justify-between py-2 px-3 border rounded-lg text-sm" data-testid={`appt-${a.id}`}>
          <div className="min-w-0">
            <div className="font-medium truncate">{a.patient_name} → {a.doctor_name}</div>
            <div className="text-xs text-muted-foreground">{a.date} {a.time} · {a.mode === "video" ? "Videoconsulta" : "Presencial"} · {a.status}</div>
          </div>
          <Button size="icon" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => remove(a.id)} data-testid={`delete-appt-${a.id}`}><Trash2 className="w-4 h-4" /></Button>
        </div>
      ))}
      {items.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">Sin citas</div>}
    </div>
  );
}

function AdminPrescriptions({ refreshKey, onChange }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/prescriptions").then(({ data }) => setItems(data)).catch(() => {}); }, [refreshKey]);
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta receta?")) return;
    try { await api.delete(`/admin/prescriptions/${id}`); toast.success("Eliminada"); onChange(); }
    catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="space-y-2 max-h-[500px] overflow-y-auto">
      {items.map((r) => (
        <div key={r.id} className="flex items-center justify-between py-2 px-3 border rounded-lg text-sm" data-testid={`rx-${r.id}`}>
          <div className="min-w-0">
            <div className="font-medium truncate">{r.code} · {r.patient_name}</div>
            <div className="text-xs text-muted-foreground">{(r.created_at || "").slice(0, 10)} · {(r.medications || []).length} med.</div>
          </div>
          <Button size="icon" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => remove(r.id)} data-testid={`delete-rx-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
        </div>
      ))}
      {items.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">Sin recetas</div>}
    </div>
  );
}

function AdminExams({ refreshKey, onChange }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/exam-orders").then(({ data }) => setItems(data)).catch(() => {}); }, [refreshKey]);
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta petición?")) return;
    try { await api.delete(`/admin/exam-orders/${id}`); toast.success("Eliminada"); onChange(); }
    catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="space-y-2 max-h-[500px] overflow-y-auto">
      {items.map((o) => (
        <div key={o.id} className="flex items-center justify-between py-2 px-3 border rounded-lg text-sm" data-testid={`exam-${o.id}`}>
          <div className="min-w-0">
            <div className="font-medium truncate">{o.code} · {o.patient_name}</div>
            <div className="text-xs text-muted-foreground">{(o.created_at || "").slice(0, 10)} · {o.type} · {(o.tests || []).length} pruebas</div>
          </div>
          <Button size="icon" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => remove(o.id)} data-testid={`delete-exam-${o.id}`}><Trash2 className="w-4 h-4" /></Button>
        </div>
      ))}
      {items.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">Sin peticiones</div>}
    </div>
  );
}

export default function AdminUsers() {
  const { t } = useI18n();
  const [refreshKey, setRefreshKey] = useState(0);
  const [dlg, setDlg] = useState({ open: false, initial: null });
  const refresh = () => setRefreshKey((k) => k + 1);

  const handleAction = (action, user) => {
    if (action === "edit") setDlg({ open: true, initial: { ...user, password: "" } });
    else refresh();
  };

  return (
    <div className="space-y-6 fade-up" data-testid="users-page">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl">Gestión completa</h1>
        <Button onClick={() => setDlg({ open: true, initial: null })} className="bg-emerald-700 hover:bg-emerald-800" data-testid="new-user-btn">
          <UserPlus className="w-4 h-4 mr-2" /> Nuevo usuario
        </Button>
      </div>

      <Tabs defaultValue="users">
        <TabsList>
          <TabsTrigger value="users" data-testid="tab-users-all">Usuarios</TabsTrigger>
          <TabsTrigger value="appointments" data-testid="tab-appointments">Citas</TabsTrigger>
          <TabsTrigger value="prescriptions" data-testid="tab-prescriptions">Recetas</TabsTrigger>
          <TabsTrigger value="exams" data-testid="tab-exams">Exámenes</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-4">
          <Tabs defaultValue="all">
            <TabsList className="mb-3">
              <TabsTrigger value="all">Todos</TabsTrigger>
              <TabsTrigger value="patient">Pacientes</TabsTrigger>
              <TabsTrigger value="doctor">Doctores</TabsTrigger>
              <TabsTrigger value="admin">Admins</TabsTrigger>
            </TabsList>
            {["all", "patient", "doctor", "admin"].map((f) => (
              <TabsContent key={f} value={f}>
                <Card><CardContent className="pt-4"><UserList filter={f} refreshKey={refreshKey} onAction={handleAction} /></CardContent></Card>
              </TabsContent>
            ))}
          </Tabs>
        </TabsContent>

        <TabsContent value="appointments" className="mt-4">
          <Card><CardHeader><CardTitle>Todas las citas</CardTitle></CardHeader><CardContent><AdminAppointments refreshKey={refreshKey} onChange={refresh} /></CardContent></Card>
        </TabsContent>

        <TabsContent value="prescriptions" className="mt-4">
          <Card><CardHeader><CardTitle>Todas las recetas</CardTitle></CardHeader><CardContent><AdminPrescriptions refreshKey={refreshKey} onChange={refresh} /></CardContent></Card>
        </TabsContent>

        <TabsContent value="exams" className="mt-4">
          <Card><CardHeader><CardTitle>Todas las peticiones</CardTitle></CardHeader><CardContent><AdminExams refreshKey={refreshKey} onChange={refresh} /></CardContent></Card>
        </TabsContent>
      </Tabs>

      <UserDialog
        open={dlg.open}
        onOpenChange={(o) => setDlg((d) => ({ ...d, open: o }))}
        initial={dlg.initial}
        onSaved={refresh}
      />
    </div>
  );
}
